import { observable, action, computed, runInAction } from "mobx";
import { DeviceDetector } from "@utils";
import { history } from "@state";

class EventSwitcherViewStore {
	//#region observables

	/** Event ids to loop over. */
	@observable eventIds: string[] = [];
	@observable eventsWithBets: {
		eventId: string;
		isButtonToggled: boolean;
	}[] = [];

	@observable currentIndex: number = 0;
	@observable lastUsedDirection: string = "right";

	@observable isNavigating: boolean = false;
	private navigationTimeout: ReturnType<typeof setTimeout> | null = null;

	//#endregion observables

	//#region computed

	@computed get isAvailable(): boolean {
		return this.eventIds?.length >= 2;
	}

	//#endregion computed

	//#region actions

	@action.bound
	onInitialize() {
		if (this.eventIds.length > 0) {
			return;
		}

		const eventIdsFromHistory =
			history.location.state?.eventIdsForEventSwitcher;

		if (!Array.isArray(eventIdsFromHistory)) {
			this.eventIds = [];
		} else {
			this.eventIds =
				history.location.state?.eventIdsForEventSwitcher || [];
		}

		this.eventsWithBets = [];

		this.eventIds.forEach((eId) => {
			const currentParams = new URLSearchParams(window.location.search);
			const isToggled =
				currentParams.get("myBets") === "true" ? true : false;

			this.eventsWithBets.push({
				eventId: eId,
				isButtonToggled: window.location.pathname.includes(eId)
					? isToggled
					: false,
			});
		});
	}

	@action.bound
	setEventList(newList: string[], currentEventIndex: number) {
		this.eventIds = newList;
		this.currentIndex = currentEventIndex;
	}

	//#endregion actions

	//#region switcher actions

	@action.bound
	moveLeft(eventId: string) {
		if (this.isNavigating || this.eventIds.length <= 1) {
			return;
		}

		this.isNavigating = true;

		if (this.navigationTimeout) {
			clearTimeout(this.navigationTimeout);
		}

		const extractedEventId = eventId.includes("-myBets=")
			? eventId.split("-")[0]
			: eventId;

		const currentIndex = this.eventIds.indexOf(extractedEventId);

		let prevIndex = currentIndex - 1;

		if (prevIndex < 0) {
			prevIndex = this.eventIds.length - 1;
		}

		this.lastUsedDirection = "left";
		const currentUrl = window.location.pathname.split("/");
		currentUrl[currentUrl.length - 1] = this.eventIds[prevIndex];
		let url = currentUrl.join("/");

		if (!DeviceDetector.isMobileTheme && !url.includes("-myBets=")) {
			url += "-myBets=false";
		}

		history.replace(url, history.location?.state);

		this.navigationTimeout = setTimeout(() => {
			runInAction(() => {
				this.isNavigating = false;
			});
		}, 300);
	}

	@action.bound
	moveRight(eventId: string) {
		if (this.isNavigating || this.eventIds.length <= 1) {
			return;
		}

		this.isNavigating = true;

		if (this.navigationTimeout) {
			clearTimeout(this.navigationTimeout);
		}

		const extractedEventId = eventId.includes("-myBets=")
			? eventId.split("-")[0]
			: eventId;

		const currentIndex = this.eventIds.indexOf(extractedEventId);

		let nextIndex = currentIndex + 1;

		if (nextIndex >= this.eventIds.length) {
			nextIndex = 0;
		}

		this.lastUsedDirection = "right";
		const currentUrl = window.location.pathname.split("/");
		currentUrl[currentUrl.length - 1] = this.eventIds[nextIndex];
		let url = currentUrl.join("/");

		if (!DeviceDetector.isMobileTheme && !url.includes("-myBets=")) {
			url += "-myBets=false";
		}

		history.replace(url, history.location?.state);

		this.navigationTimeout = setTimeout(() => {
			runInAction(() => {
				this.isNavigating = false;
			});
		}, 300);
	}

	@action.bound
	removeEventId(eventId: string) {
		if (this.eventIds.length == 0) {
			if (history.location.state) {
				history.location.state.eventIdsForEventSwitcher = this.eventIds;
			}
			return;
		}
		const extractedEventId = eventId.includes("-myBets=")
			? eventId.split("-")[0]
			: eventId;

		const currentIndex = this.eventIds.indexOf(extractedEventId);

		if (currentIndex === -1) {
			return;
		}

		let nextIndex = currentIndex + 1;

		if (nextIndex >= this.eventIds.length) {
			nextIndex = 0;
		}

		this.lastUsedDirection = "right";
		const currentUrl = window.location.pathname.split("/");
		currentUrl[currentUrl.length - 1] = this.eventIds[nextIndex];
		let url = currentUrl.join("/");

		if (!DeviceDetector.isMobileTheme && !url.includes("-myBets=")) {
			url += "-myBets=false";
		}

		this.eventIds = this.eventIds.filter((id) => id !== extractedEventId);

		this.currentIndex = this.eventIds.indexOf(extractedEventId);

		const newState = {
			...history.location.state,
			eventIdsForEventSwitcher: [...this.eventIds],
		};
		history.replace(url, newState);
	}

	@action.bound
	updateEventsWithBets(eventId: string, isButtonToggled: boolean) {
		const eventIdx = this.eventsWithBets.findIndex(
			(eventBetComb) => eventBetComb.eventId === eventId
		);

		if (eventIdx > -1) {
			this.eventsWithBets[eventIdx].isButtonToggled = isButtonToggled;
		}
	}

	//#endregion switcher actions

	//#region  disposers

	@action.bound
	onDispose() {
		this.eventIds = [];
		this.eventsWithBets = [];
		this.currentIndex = 0;
		this.isNavigating = false;
		if (this.navigationTimeout) {
			clearTimeout(this.navigationTimeout);
			this.navigationTimeout = null;
		}
	}

	//#endregion disposers
}

export default EventSwitcherViewStore;
